// components/Header.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Button,
    Avatar,
    Box,
    Divider,
    Menu,
    MenuItem,
    Tooltip,
    Badge,
    ListItemText,
    ListItemIcon,
    Chip,
} from '@mui/material';
import {
    Menu as MenuIcon,
    NotificationsOutlined,
    KeyboardArrowDown,
    AccountCircleOutlined,
    SettingsOutlined,
    LogoutOutlined,
    CheckCircleOutline as CheckIcon,
    Description as ContractIcon,
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import io from 'socket.io-client';
import axios from 'axios';

const StyledAppBar = styled(AppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    backgroundColor: '#ffffff',
    color: '#333333',
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        width: `calc(100% - 260px)`,
        marginLeft: `260px`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const UserInfoButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    padding: theme.spacing(0.5, 1),
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    padding: theme.spacing(1.5, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: '200px',
    '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
        color: theme.palette.text.secondary,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        '& .MuiSvgIcon-root': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledNotificationItem = styled(MenuItem)(({ theme, isRead }) => ({
    padding: theme.spacing(1.5, 2),
    opacity: isRead ? 0.7 : 1,
    borderLeft: isRead ? 'none' : `4px solid ${theme.palette.primary.main}`,
    backgroundColor: isRead ? 'inherit' : alpha(theme.palette.primary.main, 0.04),
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '& .MuiListItemIcon-root': {
        minWidth: '40px',
    },
    '& .MuiListItemText-primary': {
        fontWeight: isRead ? 400 : 600,
        fontSize: '0.875rem',
        color: theme.palette.text.primary,
        marginBottom: '4px'
    },
    '& .MuiListItemText-secondary': {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary
    }
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        fontSize: '10px',
        height: '20px',
        minWidth: '20px',
        padding: '0 6px',
        backgroundColor: theme.palette.error.main,
        animation: isRead => !isRead ? 'pulse 2s infinite' : 'none',
    },
    '@keyframes pulse': {
        '0%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0.4)',
        },
        '70%': {
            transform: 'scale(1.1)',
            boxShadow: '0 0 0 10px rgba(255, 0, 0, 0)',
        },
        '100%': {
            transform: 'scale(1)',
            boxShadow: '0 0 0 0 rgba(255, 0, 0, 0)',
        },
    }
}));

export default function Header({ open, handleDrawerOpen, onLogout, headerTitle }) {
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifAnchorEl, setNotifAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const openMenu = Boolean(anchorEl);
    const openNotifMenu = Boolean(notifAnchorEl);

    

    const userInitials = userData?.name
        ? userData.name.split(' ').map(n => n[0]).join('').toUpperCase()
        : '';

    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            setUserData(JSON.parse(storedUser));
        }
    }, []);

    const avatarUrl = `${process.env.REACT_APP_API_URL}/uploads/avatars/${userData?.avatar}`

    // Socket.IO connection
    useEffect(() => {
        // Socket.IO connection for real-time notifications
        const socket = io(`${process.env.REACT_APP_API_URL}`);
        const user = JSON.parse(localStorage.getItem('user'));

        if (user) {
            // Join user-specific notification room
            socket.emit('joinRoom', `user_${user.id}`);

            // Listen for new notifications
            socket.on('newNotification', (notification) => {
                setNotifications(prev => [notification, ...prev]);
                setUnreadCount(prev => prev + 1);

                // Show browser notification if supported
                if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('New Contract Created', {
                        body: notification.message,
                        icon: '/logo192.png' // Make sure this path exists in your public folder
                    });
                }
            });
        }

        // Cleanup on unmount
        return () => socket.disconnect();
    }, []);

    // Fetch initial notifications
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const [notifRes, countRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/notifications`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${process.env.REACT_APP_API_URL}/notifications/unread-count`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                setNotifications(notifRes.data.notifications);
                setUnreadCount(countRes.data.count);
            } catch (error) {
                console.error('Error fetching notifications:', error);
            }
        };

        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
            fetchNotifications();
            
            // Request notification permission if not already granted
            if ('Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission();
            }
        }
    }, []);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = async () => {
        handleMenuClose();

        try {
            const token = localStorage.getItem('authToken');
            const user = JSON.parse(localStorage.getItem('user'));
            const userId = user?.id;

            if (userId) {
                const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ userId })
                });

                if (!res.ok) {
                    console.error('Logout API failed:', res.status);
                }
            }

            // Clean up local storage regardless of API response
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');

            // Call onLogout prop to sync state
            if (onLogout) {
                onLogout();
            } else {
                // Fallback to direct navigation
                window.location.href = '/login';
            }
        } catch (error) {
            console.error('Logout failed:', error);
            // Clean up and redirect even if API call fails
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
    };

    const handleNotifMenuOpen = (event) => {
        setNotifAnchorEl(event.currentTarget);
    };

    const handleNotifMenuClose = () => {
        setNotifAnchorEl(null);
    };    const handleNotificationClick = async (notificationId, relatedId) => {
        try {
            const token = localStorage.getItem('authToken');
            // Mark notification as read
            await axios.post(
                `${process.env.REACT_APP_API_URL}/notifications/${notificationId}/read`,
                {},
                { headers: { Authorization: `Bearer ${token}` }}
            );
            
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? { ...n, status: 'read' } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));

            // Close notification menu
            handleNotifMenuClose();

            // Navigate to contract details if relatedId exists
            if (relatedId) {
                navigate(`/contracts/${relatedId}`);
            }
        } catch (error) {
            console.error('Error handling notification:', error);
        }
    };

    return (
        <StyledAppBar position="fixed" open={open}>
          
            <Toolbar>
                {/* <Typography variant="h6" noWrap component="div" sx={{ display: { xs: 'none', sm: 'block' }, ml: 2, mt: 1, mb: 1, fontWeight: 600 }}>
                    {headerTitle || 'JnP Contract Management'}
                </Typography> */}
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    onClick={handleDrawerOpen}
                    edge="start"
                    sx={{ mr: 2, ...(open && { display: 'none' }) }}
                >
                    <MenuIcon />
                </IconButton>

                <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                    {/* Contract Management System */}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {/* Notifications icon with badge and dropdown */}
                    <Tooltip title="Notifications">
                        <IconButton
                            color="inherit"
                            size="large"
                            sx={{ mr: 1 }}
                            onClick={handleNotifMenuOpen}
                            aria-controls={openNotifMenu ? 'notif-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={openNotifMenu ? 'true' : undefined}
                        >
                            <StyledBadge badgeContent={unreadCount} color="error">
                                <NotificationsOutlined />
                            </StyledBadge>
                        </IconButton>
                    </Tooltip>

                    <Menu
                        id="notif-menu"
                        anchorEl={notifAnchorEl}
                        open={openNotifMenu}
                        onClose={handleNotifMenuClose}
                        PaperProps={{
                            elevation: 3,
                            sx: {
                                width: 360,
                                maxHeight: 480,
                                mt: 1.5,
                                overflow: 'auto',
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Notifications
                                {unreadCount > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${unreadCount} new`}
                                        color="primary"
                                        sx={{ ml: 1, height: 20 }}
                                    />
                                )}
                            </Typography>
                        </Box>

                        {notifications.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <NotificationsOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <Typography color="text.secondary">
                                    No notifications yet
                                </Typography>
                            </Box>
                        ) : (                            notifications.map((notification) => (
                                <StyledNotificationItem
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification.id, notification.related_id)}
                                    isRead={notification.status === 'read'}
                                >
                                    <ListItemIcon>
                                        {notification.type === 'CONTRACT_CREATED' ? (
                                            <ContractIcon color="primary" />
                                        ) : (
                                            <CheckIcon color="success" />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={notification.message}
                                        secondary={new Date(notification.created_at).toLocaleString('en-US', {
                                            weekday: 'short',
                                            day: 'numeric',
                                            month: 'short',
                                            hour: 'numeric',
                                            minute: 'numeric'
                                        })}
                                    />
                                </StyledNotificationItem>
                            ))
                        )}
                        
                        {notifications.length > 0 && (
                            <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
                                <Button
                                    fullWidth
                                    size="small"
                                    sx={{ textTransform: 'none' }}
                                    onClick={() => {
                                        // Clear all notifications logic here
                                        handleNotifMenuClose();
                                    }}
                                >
                                    Clear all
                                </Button>
                            </Box>
                        )}
                    </Menu>

                    <Divider orientation="vertical" flexItem sx={{ height: 28, mx: 1 }} />

                    {/* User profile section */}
                    <UserInfoButton
                        onClick={handleMenuOpen}
                        aria-controls={openMenu ? 'user-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={openMenu ? 'true' : undefined}
                        endIcon={<KeyboardArrowDown />}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                fontSize: "12px",
                                height: 36,
                                mr: 1,
                                bgcolor: avatarUrl ? 'transparent' : 'primary.main'
                            }}
                            src={avatarUrl}
                            alt={userData?.name}
                        >
                            {!avatarUrl && userInitials}
                        </Avatar>

                        <Box sx={{ textAlign: 'left', display: { xs: 'none', sm: 'block' } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                                {userData?.name || 'User Name'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                {userData?.email || 'N/A'}
                            </Typography>
                        </Box>
                    </UserInfoButton>

                    {/* User dropdown menu */}
                    <Menu
                        id="user-menu"
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        PaperProps={{
                            elevation: 3,
                            sx: {
                                mt: 1.5,
                                overflow: 'visible',
                                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.15))',
                                '&:before': {
                                    content: '""',
                                    display: 'block',
                                    position: 'absolute',
                                    top: 0,
                                    right: 14,
                                    width: 10,
                                    height: 10,
                                    bgcolor: 'background.paper',
                                    transform: 'translateY(-50%) rotate(45deg)',
                                    zIndex: 0,
                                },
                            },
                        }}
                    >
                        {/* <StyledMenuItem onClick={handleMenuClose}>
                            <AccountCircleOutlined />
                            <Typography variant="body2">Profile</Typography>
                        </StyledMenuItem>
                        <StyledMenuItem onClick={handleMenuClose}>
                            <SettingsOutlined />
                            <Typography variant="body2">Settings</Typography>
                        </StyledMenuItem>
                        <Divider sx={{ my: 1 }} /> */}
                        <StyledMenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                            <LogoutOutlined sx={{ color: 'inherit !important' }} />
                            <Typography variant="body2">Logout</Typography>
                        </StyledMenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </StyledAppBar>
    );
}
